import React, { useEffect, useState, useContext, useRef } from 'react';
import { ChatsIcon, CrosshairIcon, DeviceMobileIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/el";
import "dayjs/locale/es";
import { recordStat } from '@/shared/functions';
import { StatsContext } from '@/app/contexts';
import { TpTooltip } from "@tranquility-ai/theme-components/tooltip";
import { PanelListCard } from "@tranquility-ai/theme-components/panel-list-card";
import { Spinner } from '@heroui/spinner';
import { SidePanel, SIDE_PANEL_LIST_CLASS, SIDE_PANEL_LIST_SCROLL_CLASS, SIDE_PANEL_LIST_SCROLL_SYMMETRIC_CLASS, SIDE_PANEL_SYMMETRIC_INSET_CLASS } from './side-panel';
import { getSessionLatestQuestion } from '@/shared/chat-archive-sessions';
import { useOptionalI18n } from '@/i18n/LocaleProvider';
import { useTimePilotT } from '@/i18n/useTimePilotT';

dayjs.extend(relativeTime);

export const CHAT_SESSIONS_PANEL_WIDTH_PX = 330;

interface ChatSessionsProps {
  caseId: string;
  userId: string;
  sessionId: string | null;
  switchToSession: (sessionId: string) => void;
  closeSessions: () => void;
  chatSessions: any[] | null;
  onRefresh?: (options?: { showLoading?: boolean; expectedSessionId?: string | null }) => void | Promise<void>;
  showMobileAnalysis?: boolean;
}

export const ChatSessions: React.FC<ChatSessionsProps> = (
  {
    sessionId,
    switchToSession,
    closeSessions,
    chatSessions,
    onRefresh,
    showMobileAnalysis = false,
  }: ChatSessionsProps
) => {
  const t = useTimePilotT();
  const locale = useOptionalI18n()?.locale ?? 'en';
  const [chatHistorySessions, setChatHistorySessions] = useState<any>(null);
  const [localSessionId, setLocalSessionId] = useState(sessionId);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    setLocalSessionId(sessionId);
  }, [sessionId]);

  useEffect(() => {
    setChatHistorySessions(chatSessions);
  }, [chatSessions]);

  useEffect(() => {
    void onRefreshRef.current?.({ expectedSessionId: sessionId });
  }, [sessionId]);

  const statsContext = useContext(StatsContext);

  const sessionCount = chatHistorySessions?.length ?? 0;
  const hasSessions = sessionCount > 0;

  return (
    <SidePanel
      side="left"
      title={t('chat.sessions.title')}
      className={`min-h-0 flex-1 ${SIDE_PANEL_SYMMETRIC_INSET_CLASS}`}
      onCollapse={closeSessions}
      collapseLabel={t('chat.sessions.close')}
      sectionLabel={hasSessions ? t('chat.sessions.recent') : undefined}
      aria-label={t('chat.sessions.title')}
    >
      <div className={`custom-scrollbar2 ${SIDE_PANEL_LIST_SCROLL_CLASS} ${SIDE_PANEL_LIST_SCROLL_SYMMETRIC_CLASS}`}>
        {hasSessions ? (
          <ol className={SIDE_PANEL_LIST_CLASS}>
            {chatHistorySessions.map((session: any) => {
              const isCurrent = session.sessionId === localSessionId;
              const lastQuestion = getSessionLatestQuestion(session);
              const lastEpoch = lastQuestion?.epochTime;

              return (
                <li key={`chat-session-${session.sessionId}`}>
                  <TpTooltip
                    content={(
                      <div className="flex flex-col gap-1 py-0">
                        <span className="text-sm font-semibold text-[var(--text-brand)]">
                          {t(isCurrent ? 'chat.sessions.currentPrompts' : 'chat.sessions.prompts')}
                        </span>
                        <ol className="m-0 mt-0 list-outside list-decimal pl-4">
                          {session?.questions?.map((question: any, index: number) => (
                            <li key={index} className="text-balance">
                              <span>{question.question}</span>
                              {question.isDeepDive && (
                                <CrosshairIcon size={14} className="relative -mb-[2px] mt-[2px] text-[var(--text-brand)]" />
                              )}
                              {showMobileAnalysis && question.isMobile && (
                                <DeviceMobileIcon size={14} className="relative -mb-[2px] mt-[2px] text-[var(--text-brand)]" />
                              )}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    delayDuration={500}
                    placement="right"
                  >
                    <PanelListCard
                      data-id={`sessionId-${session.sessionId}`}
                      isSelected={isCurrent}
                      ariaCurrent={isCurrent}
                      title={lastQuestion?.question ?? t('chat.sessions.untitled')}
                      meta={
                        lastEpoch ? (
                          <time
                            dateTime={new Date(lastEpoch * 1000).toISOString()}
                            title={new Date(lastEpoch * 1000).toLocaleString(locale)}
                          >
                            {dayjs(lastEpoch * 1000).locale(locale).fromNow()}
                          </time>
                        ) : (
                          '--'
                        )
                      }
                      badge={
                        <>
                          <ChatsIcon size={16} weight="regular" aria-hidden />
                          {session?.questions?.length ?? 0}
                          <span className="sr-only">
                            {t((session?.questions?.length ?? 0) === 1
                              ? 'chat.sessions.prompt'
                              : 'chat.sessions.promptPlural')}
                          </span>
                        </>
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!isCurrent) {
                          switchToSession(session.sessionId);
                          recordStat(
                            'SwitchToSessionButtonClicked',
                            event,
                            statsContext,
                          );
                        }
                      }}
                    />
                  </TpTooltip>
                </li>
              );
            })}
          </ol>
        ) : (
          <div className="flex min-h-[120px] w-full items-center justify-center">
            {!chatHistorySessions ? (
              <Spinner size="md" color="white" className="bg-transparent" />
            ) : (
              <p
                data-testid="session-history-empty"
                className="m-0 text-sm font-light text-[var(--text-inverse,#ffffff)]/70"
              >
                {t('chat.sessions.empty')}
              </p>
            )}
          </div>
        )}
      </div>
    </SidePanel>
  );
};
